import { Suspense, useState } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useTicketEvents, TicketCreatedEvent } from '@/hooks/use-ticket-events';
import { InboxSidebar } from './components/inbox-sidebar';
import { InboxProvider, useInbox } from './inbox-context';
import { useAuth } from '@/providers/auth-provider';

function InboxLayoutContent({ children }: { children: React.ReactNode }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const pathname = useLocation().pathname;
  const { profile } = useAuth();

  const customFolderId = searchParams.get('customFolder');
  let selectedFolder = customFolderId
    ? `custom:${customFolderId}`
    : searchParams.get('folder') || 'all';

  if (pathname.includes('/archived')) {
    selectedFolder = 'archived';
  }

  const { folderCounts, loadingCounts, incrementFolderCounts } = useInbox();
  const queryClient = useQueryClient();
  const [highlightedFolder, setHighlightedFolder] = useState<string | null>(null);

  useTicketEvents({
    userId: profile?.id,
    onTicketCreated: (event: TicketCreatedEvent) => {
      incrementFolderCounts(event.affectedFolders);
      if (event.affectedFolders.length > 0) {
        setHighlightedFolder(event.affectedFolders[0]);
      }
    },
  });

  const handleFolderSelect = (folder: string) => {
    if (highlightedFolder === folder) {
      setHighlightedFolder(null);
    }

    if (folder === selectedFolder) {
      queryClient.invalidateQueries({ queryKey: ['inbox-tickets'] });
      return;
    }

    if (folder === 'archived') {
      navigate('/social-manager/inbox/archived');
    } else if (folder.startsWith('custom:')) {
      const folderId = folder.replace('custom:', '');
      const params = new URLSearchParams();
      params.set('customFolder', folderId);
      navigate(`/social-manager/inbox?${params.toString()}`, { replace: true });
    } else {
      const params = new URLSearchParams();

      if (folder !== 'all') {
        params.set('folder', folder);
      }

      navigate(`/social-manager/inbox?${params.toString()}`, { replace: true });
    }
  };

  return (
    <div className="flex h-full w-full min-w-0">
      <InboxSidebar
        selectedFolder={selectedFolder}
        counts={folderCounts}
        loading={loadingCounts}
        onFolderSelect={handleFolderSelect}
        highlightedFolder={highlightedFolder}
      />
      <div className="flex-1 min-w-0 overflow-hidden">{children}</div>
    </div>
  );
}

export default function InboxLayout({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<div className="flex items-center justify-center h-full">Loading...</div>}>
      <InboxProvider>
        <InboxLayoutContent>{children}</InboxLayoutContent>
      </InboxProvider>
    </Suspense>
  );
}
